/**
 * Created by Alican Köker on 2018-10-11.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite, TweenLite} from "gsap";
import {FruitsGameConfig} from "../../FruitsGameConfig";
import {FruitsSoundConfig} from "../../FruitsSoundConfig";
import {FruitsReels} from "../../reel/reelarea/FruitsReels";
import {FruitsReelSymbol} from "../../reelsymbol/FruitsReelSymbol";
import {FruitsHelper} from "../../utils/FruitsHelper";

export interface FruitsStickySymbol {
    symName:string,
    index:number,
}

export class FruitsStickyRespinOverlayView extends BaseView {

    private _layer:ScalableLayer;
    private _container:PIXI.Container;

    private _orientation:Orientation = Orientation.LANDSCAPE;

    private _stickySymbols:FruitsStickySymbol[];
    public static readonly REMOVE_STICKY_SYMBOLS:string = "stickyRespinOverlayView_RemoveStickySymbols";
    private _stickyFxFilter:PIXI.filters.ColorMatrixFilter;

    constructor() {
        super();

        this._layer = StageManager.getLayer(FruitsGameConfig.instance.LAYERS.STICKY_RESPIN_OVERLAY.name);
        this._stickySymbols = [];

        this._stickyFxFilter = new PIXI.filters.ColorMatrixFilter();

        this._container = new PIXI.Container();
        this._layer.addChild(this._container);

        EventHandler.addEventListener(this, FruitsStickyRespinOverlayView.REMOVE_STICKY_SYMBOLS, (event:GameEvent) => this.initiateVisualObjects());
    }

    protected onResize(resizeData:IResizeData):void {
        EventHandler.dispatchEvent(new GameEvent(FruitsReels.SET_ORIENTATION, resizeData.orientation));

        this._orientation = resizeData.orientation;

        this.initiateVisualObjects();

        this._stickySymbols.forEach((symbolData) => {
            this.stickSymbol(symbolData.symName, symbolData.index);
        })
    }

    protected initAnimations():void {
        this.initiateVisualObjects();
    }

    private createStuff():PIXI.Sprite {

        let stuff:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("sym_Mystery")[0]);

        let reelIndexes:[number, number] = FruitsHelper.translateReelIndex(3);
        let symbolOverlayObjectPos:number[] = FruitsHelper.getSymbolPosition(reelIndexes[0], reelIndexes[1], this._orientation, "NORMAL");

        stuff.position.set(symbolOverlayObjectPos[0], symbolOverlayObjectPos[1]);
        stuff.anchor.set(0.5, 0.5);
        stuff.scale.set(0, 0);

        this._container.addChild(stuff);
        return stuff;
    }

    public getStuffAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        let stuff = this.createStuff();
        stuff.alpha = 0.5;
        tl.add(TweenLite.fromTo(stuff.scale, 4, {x : 0, y : 0}, {x : 1, y : 1}));

        return tl;
    }

    private getHue(symbol:string):number {

        switch(symbol) {
            case "M1":
                return -30;

            case "M2":
            case "M3":
            case "L1":
                return 0;

            case "M4":
            case "L2":
                return 80;

            case "M5":
            case "M6":
            case "L3":
                return 125;

            case "L4":
                return -126;

            default:
                return 0;
        }
    }

    public getSmallStickySymAnimation(symName:string, symIndex:number, playSound:boolean):TimelineLite {
        let sym:FruitsReelSymbol = <FruitsReelSymbol>Reels.getSymbol(symIndex, 0);

        let reelIndexes:[number, number] = FruitsHelper.translateReelIndex(symIndex);
        let symbolOverlayObjectPos:number[] = FruitsHelper.getSymbolPosition(reelIndexes[0], reelIndexes[1], this._orientation, "NORMAL");

        const stickyFxSplash = new TimelineSprite(GameResources.getTextures("StickyFxSplash"));
        stickyFxSplash.hide();
        stickyFxSplash.anchor.set(0.5, 0.5);
        stickyFxSplash.scale.set(0.5, 0.5);
        stickyFxSplash.position.set(symbolOverlayObjectPos[0], symbolOverlayObjectPos[1]);

        stickyFxSplash.filters = [this._stickyFxFilter];

        this._stickyFxFilter.hue(this.getHue(symName), true);

        this._container.addChild(stickyFxSplash);

        const symTl:TimelineLite = new TimelineLite();
        symTl.add(TweenLite.to(sym.scale, 0.05, {x : 1.3, y : 1.3}));
        symTl.add(TweenLite.to(sym.scale, 0.05, {x : 0.9, y : 0.9}));
        symTl.add(TweenLite.to(sym.scale, 0.1, {x : 1, y : 1}));

        const tl:TimelineLite = new TimelineLite();

        tl.add(stickyFxSplash.getAnimationAutoShowHide(true, false, [0, 5]));
        tl.add([
            stickyFxSplash.getAnimationAutoShowHide(true, true, [5, 19]),
            () => {if(symIndex > 3) FruitsStickyRespinOverlayView.playSymbolTransitionSound(playSound)},
            symTl
        ]);
        if(sym.symName.substr(0, 1) !== "S")
            tl.add(sym.changeState({
                state : FruitsGameConfig.instance.SYMBOL_STATES.respin,
                fadeInDuration : 0.15,
                setStack : false
            }), 0.1);

        tl.add(() => this._container.removeChild(stickyFxSplash));

        return tl;

    }

    private static playSymbolTransitionSound(playSound:boolean) {
        if(playSound) {
            SlotGame.sound.playEffect(FruitsSoundConfig.STICKY_SYMBOL_TRANSITION);
        }
    }

    public stickSymbol(symName:string, symIndex:number):void {
        let symbol:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(symName + "/" + symName + "_respin")[0]);

        let reelIndexes:[number, number] = FruitsHelper.translateReelIndex(symIndex);
        let symbolOverlayObjectPos:number[] = FruitsHelper.getSymbolPosition(reelIndexes[0], reelIndexes[1], this._orientation, "NORMAL");

        symbol.position.set(symbolOverlayObjectPos[0], symbolOverlayObjectPos[1]);
        symbol.anchor.set(.5, .5);

        let symbolObject:FruitsStickySymbol = {
            symName : symName,
            index : symIndex,
        };

        this._stickySymbols.push(symbolObject);

        this._container.addChild(symbol);
    }

    public initiateVisualObjects():void {
        this._container.removeChildren();
        this._stickySymbols = [];
    }

    public stickInitialSymbols(positions:boolean[], featuredSymbol:string) {
        let playSound:boolean = true;

        for(let i = 3; i < positions.length; i++) {
            if(positions[i]) {
                this.getSmallStickySymAnimation(featuredSymbol, i, playSound);
                playSound = false;
            }
        }
    }
}